import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { useToast } from '../hooks/useToast';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const { showToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      console.log('Auth callback processing started');

      try {
        // Initialize default values
        let redirectPath = '/';
        let pendingValuationId = null;

        console.log('Auth callback - checking for state preservation...');
        console.log('Current URL:', window.location.href);

        // Primary method: Check sessionStorage for OAuth state backup
        const oauthStateBackup = sessionStorage.getItem('oauth_state_backup');
        const oauthStartTime = sessionStorage.getItem('oauth_start_time');
        
        if (oauthStateBackup && oauthStartTime) {
          const startTime = parseInt(oauthStartTime, 10);
          const now = Date.now();
          
          // Check if the OAuth flow was recent (within 10 minutes)
          if (now - startTime < 10 * 60 * 1000) {
            try {
              const stateData = JSON.parse(oauthStateBackup);
              console.log('Found OAuth state backup in sessionStorage:', stateData);
              
              if (stateData.redirectPath) {
                redirectPath = stateData.redirectPath;
                console.log('Using redirect path from backup:', redirectPath);
              }
              
              if (stateData.valuationId) {
                pendingValuationId = stateData.valuationId;
                console.log('Found pending valuation ID from backup:', pendingValuationId);
              }
            } catch (parseError) {
              console.error('Error parsing OAuth state backup:', parseError);
            }
          } else {
            console.log('OAuth state backup is too old, ignoring');
          }
        } else {
          console.log('No OAuth state backup found in sessionStorage');
        }

        // Secondary method: Check URL hash/query for any state (fallback)
        if (!pendingValuationId) {
          console.log('Checking URL for state parameters as fallback...');
          
          // Check hash params
          if (window.location.hash) {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            console.log('Hash params found:', [...hashParams.entries()]);
          }
          
          // Check query params  
          if (window.location.search) {
            const queryParams = new URLSearchParams(window.location.search);
            console.log('Query params found:', [...queryParams.entries()]);
          }
        }

        // Add delay to ensure session is properly set
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the current session after potentially setting it from hash params
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError('Authentication failed: ' + sessionError.message);
          showToast({
            type: 'error',
            message: 'Authentication failed. Please try again.'
          });
          navigate('/login', { replace: true });
          return;
        }

        if (session?.user) {
          console.log('Session found, user authenticated:', session.user.email);

          // Fetch the user's profile data
          const { data: profile, error: profileError } = await supabase.client
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            console.log('Creating user from session data instead');

            // Even if profile fetch fails, we can create a basic user object from auth data
            setUser({
              id: session.user.id,
              user_id: session.user.id,
              full_name: session.user.user_metadata?.full_name || null,
              name: session.user.user_metadata?.name || null,
              email: session.user.email || null,
              avatar_url: session.user.user_metadata?.avatar_url || null,
              company_name: session.user.user_metadata?.company_name || null,
              subscription_tier: 'free',
              subscription_ends_at: null,
              created_at: session.user.created_at,
              updated_at: session.user.last_sign_in_at || session.user.created_at
            }, session.access_token);
          } else if (profile) {
            console.log('Profile found, setting user data');

            // Use profile data with user auth data
            setUser({
              ...profile,
              email: session.user.email || null,
              created_at: session.user.created_at,
              updated_at: session.user.last_sign_in_at || session.user.created_at
            }, session.access_token);
          }

          // Show success toast
          showToast({
            type: 'success',
            message: 'Successfully signed in!'
          });

          // Check for backup valuation ID in sessionStorage if not found in state
          if (!pendingValuationId) {
            const backupValuationId = sessionStorage.getItem('pending_valuation_id');
            if (backupValuationId) {
              console.log('Found backup valuation ID in sessionStorage:', backupValuationId);
              pendingValuationId = backupValuationId;
            }
          }
          
          // Clean up OAuth flow tracking (but keep oauth_start_time for valuation restoration)
          sessionStorage.removeItem('oauth_state_backup');
          sessionStorage.removeItem('pending_valuation_id');
          // Note: oauth_start_time is kept for valuation data restoration

          // Determine where to redirect
          let finalRedirectPath = redirectPath;
          if (pendingValuationId) {
            console.log('Found pending valuation ID:', pendingValuationId);
            // For now, redirect back to valuation page where the wizard can continue
            // TODO: Create a proper results route when ready
            finalRedirectPath = `/valuation`;
          }

          console.log('Redirecting to:', finalRedirectPath);
          navigate(finalRedirectPath, { replace: true });
        } else {
          console.error('No user found in session');
          setError('No user found in session');
          showToast({
            type: 'error',
            message: 'Authentication failed. Please try again.'
          });
          navigate('/login', { replace: true });
        }
      } catch (err: any) {
        console.error('Auth callback error:', err?.message || 'Unknown error');
        setError(err?.message || 'Authentication failed');
        showToast({
          type: 'error',
          message: 'Authentication failed. Please try again.'
        });
        navigate('/login', { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, setUser, showToast]);

  // Basic loading and error UI
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {isProcessing ? 'Processing Login...' : error ? 'Authentication Error' : 'Success!'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isProcessing
              ? 'Please wait while we complete your authentication...'
              : error
                ? `Error: ${error}`
                : 'You have been successfully authenticated.'}
          </p>
        </div>

        {isProcessing && (
          <div className="flex justify-center items-center mt-5">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>
    </div>
  );
}